import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import RequireAuth from 'features/auth/components/RequireAuth';

// Manager関連
import ManagerPage from 'pages/manager/ManagerPage';
import ManagerLogin from 'pages/manager/Login';
import ManagerDashboard from 'pages/manager/Dashboard';
import ManagerMemberSearch from 'pages/manager/member/Search';
import ManagerMemberEdit from 'pages/manager/member/Edit';
import ManagerMemberAdd from 'pages/manager/member/Add';
import ManagerSupporterSearch from 'pages/manager/supporter/Search';
import ManagerSupporterEdit from 'pages/manager/supporter/Edit';
import ManagerSupporterAdd from 'pages/manager/supporter/Add';
import ManagerUpdateHistory from 'pages/manager/update_history/Search';
import ManagerUpdateDiff from 'pages/manager/update_history/Diff';
import ManagerContractSearch from 'pages/manager/contract/Search';
import ManagerContractEdit from 'pages/manager/contract/Edit';
import ManagerLoginIdSearch from 'pages/manager/login_id/Search';
import ManagerLoginIdAdd from 'pages/manager/login_id/Add';
import ManagerLoginIdEdit from 'pages/manager/login_id/Edit';
import ManagerLoginIdMemberLink from 'pages/manager/login_id/MemberLink';

// Supporter関連
import SupporterPage from 'pages/supporter/SupporterPage';
import SupporterLogin from 'pages/supporter/Login';
import SupporterHome from 'pages/supporter/Home';
import SupporterRescueReport from 'pages/supporter/RescueReport';
import SupporterRescueReportSearch from 'pages/supporter/rescue_report/Search';
import SupporterRescueReportAdd from 'pages/supporter/rescue_report/Add';

// Member関連
import MemberLogin from 'pages/member/Login';
import MemberHome from 'pages/member/Home';
import MemberService from 'pages/member/Service';
import MemberRescue from 'pages/member/Rescue';
import MemberPage from 'pages/member/MemberPage';
import MemberSampleForm from 'pages/member/SmapleForm';

// ルートパス定数
export const ROUTES = {
  LOGIN: '/login',
  MEMBER: '/',
  USER: '/user',
  SUPPORTER: '/supporter',
  MANAGER: '/manager',
} as const;

// ロール定数
export const ROLES = {
  MEMBER: 'member',
  USER: 'user',
  SUPPORTER: 'supporter',
  MANAGER: 'manager',
} as const;

const App: React.FC = () => {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        {/* Manager関連 */}
        <Route
          path={`${ROLES.MANAGER}${ROUTES.LOGIN}`}
          element={<ManagerLogin />}
        />
        <Route
          path={`${ROUTES.MANAGER}/mypage`}
          element={
            <RequireAuth role={ROLES.MANAGER}>
              <ManagerPage />
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.MANAGER}
          element={
            <RequireAuth role={ROLES.MANAGER}>
              <ManagerDashboard />
            </RequireAuth>
          }
        />
        <Route
          path={`${ROLES.MANAGER}/update-history`}
          element={
            <RequireAuth role={ROLES.MANAGER}>
              <ManagerUpdateHistory />
            </RequireAuth>
          }
        />
        <Route
          path={`${ROLES.MANAGER}/update-history/diff/:id`}
          element={
            <RequireAuth role={ROLES.MANAGER}>
              <ManagerUpdateDiff />
            </RequireAuth>
          }
        />
        <Route
          path={`${ROLES.MANAGER}/member`}
          element={
            <RequireAuth role={ROLES.MANAGER}>
              <ManagerMemberSearch />
            </RequireAuth>
          }
        />
        <Route
          path={`${ROLES.MANAGER}/member/add`}
          element={
            <RequireAuth role={ROLES.MANAGER}>
              <ManagerMemberAdd />
            </RequireAuth>
          }
        />
        <Route
          path={`${ROLES.MANAGER}/member/edit/:id`}
          element={
            <RequireAuth role={ROLES.MANAGER}>
              <ManagerMemberEdit />
            </RequireAuth>
          }
        />
        <Route
          path={`${ROLES.MANAGER}/supporter`}
          element={
            <RequireAuth role={ROLES.MANAGER}>
              <ManagerSupporterSearch />
            </RequireAuth>
          }
        />
        <Route
          path={`${ROLES.MANAGER}/supporter/add`}
          element={
            <RequireAuth role={ROLES.MANAGER}>
              <ManagerSupporterAdd />
            </RequireAuth>
          }
        />
        <Route
          path={`${ROLES.MANAGER}/supporter/edit/:id`}
          element={
            <RequireAuth role={ROLES.MANAGER}>
              <ManagerSupporterEdit />
            </RequireAuth>
          }
        />
        <Route
          path={`${ROLES.MANAGER}/contract`}
          element={
            <RequireAuth role={ROLES.MANAGER}>
              <ManagerContractSearch />
            </RequireAuth>
          }
        />
        <Route
          path={`${ROLES.MANAGER}/contract/edit/:id`}
          element={
            <RequireAuth role={ROLES.MANAGER}>
              <ManagerContractEdit />
            </RequireAuth>
          }
        />
        <Route
          path={`${ROLES.MANAGER}/login-id`}
          element={
            <RequireAuth role={ROLES.MANAGER}>
              <ManagerLoginIdSearch />
            </RequireAuth>
          }
        />
        <Route
          path={`${ROLES.MANAGER}/login-id/add`}
          element={
            <RequireAuth role={ROLES.MANAGER}>
              <ManagerLoginIdAdd />
            </RequireAuth>
          }
        />
        <Route
          path={`${ROLES.MANAGER}/login-id/edit/:id`}
          element={
            <RequireAuth role={ROLES.MANAGER}>
              <ManagerLoginIdEdit />
            </RequireAuth>
          }
        />
        <Route
          path={`${ROLES.MANAGER}/login-id/:id/member-link`}
          element={
            <RequireAuth role={ROLES.MANAGER}>
              <ManagerLoginIdMemberLink />
            </RequireAuth>
          }
        />
        {/* Manager関連 end */}

        {/* Supporter関連 */}
        <Route
          path={`${ROLES.SUPPORTER}${ROUTES.LOGIN}`}
          element={<SupporterLogin />}
        />
        <Route
          path={ROUTES.SUPPORTER}
          element={
            <RequireAuth role={ROLES.SUPPORTER}>
              <SupporterHome />
            </RequireAuth>
          }
        />
        <Route
          path={`${ROUTES.SUPPORTER}/rescue-report`}
          element={
            <RequireAuth role={ROLES.SUPPORTER}>
              <SupporterRescueReport />
            </RequireAuth>
          }
        />
        <Route
          path={`${ROUTES.SUPPORTER}/rescue-report/search`}
          element={
            <RequireAuth role={ROLES.SUPPORTER}>
              <SupporterRescueReportSearch />
            </RequireAuth>
          }
        />
        <Route
          path={`${ROUTES.SUPPORTER}/rescue-report/add`}
          element={
            <RequireAuth role={ROLES.SUPPORTER}>
              <SupporterRescueReportAdd />
            </RequireAuth>
          }
        />
        <Route
          path={`${ROUTES.SUPPORTER}/page`}
          element={
            <RequireAuth role={ROLES.SUPPORTER}>
              <SupporterPage />
            </RequireAuth>
          }
        />
        {/* Supporter関連 end */}

        {/* Member関連 */}
        <Route
          path={ROUTES.LOGIN}
          element={<MemberLogin />}
        />
        <Route
          path={`${ROUTES.MEMBER}welcome`}
          element={<MemberPage />}
        />
        <Route
          path={`${ROUTES.MEMBER}sample-form`}
          element={<MemberSampleForm />}
        />
        <Route
          path={`${ROUTES.MEMBER}service/*`}
          element={
            <RequireAuth role={ROLES.MEMBER}>
              <MemberService />
            </RequireAuth>
          }
        />
        <Route
          path={`${ROUTES.MEMBER}rescue/*`}
          element={
            <RequireAuth role={ROLES.MEMBER}>
              <MemberRescue />
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.MEMBER}
          element={
            <RequireAuth role={ROLES.MEMBER}>
              <MemberHome />
            </RequireAuth>
          }
        />
        {/* Member関連 end */}

        {/* リダイレクトルート */}
        <Route path="/cms/*" element={<Navigate to="/manager" replace />} />
        <Route path="/company-staff/*" element={<Navigate to="/supporter" replace />} />
      </Routes>
    </Router>
  );
};

export default App;
